import PdfViewer from '@/shared/components/PDF';
import {Box} from '@mui/material';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {useEffect, useMemo, useState} from 'react';
import {PrintableService} from '@/utils/services/organization-printables';
import {toast} from 'react-toastify';
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
import Loader from '@/shared/components/Loader';
import {PDFDocument} from 'pdf-lib';
import {getAuctionList, getAuctionListSelector} from '@/store/organization-auction';
import {EVENT_ID} from '@/utils/constants';
import {auctionStyle} from '../../Auction/Auction.styles';
import EmptyContent from '@/shared/components/Table/EmptyContent';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';

const BidSheets = () => {
	const dispatch = useAppDispatch();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const auctionList = useAppSelector(getAuctionListSelector);

	const itemIds = useMemo(() => {
		return auctionList?.data.map((item) => item.id);
	}, [auctionList]);

	const [pdfData, setPdfData] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const mergePDFs = async (pdfBuffers: Array<ArrayBuffer>) => {
		const mergedPdf = await PDFDocument.create();

		/* eslint-disable no-await-in-loop */
		for (const pdfBytes of pdfBuffers) {
			const pdf = await PDFDocument.load(pdfBytes);
			const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
			copiedPages.forEach((page) => mergedPdf.addPage(page));
		}

		return mergedPdf.save();
	};

	const getQRCode = async () => {
		if (!currentEvent || !itemIds?.length) return;
		setIsLoading(true);

		try {
			const data = await Promise.all(itemIds.map((id) => PrintableService.getBigSheet(id)));

			const pdfBuffers = data.map(({data}) => data);

			const mergedPdfBytes = await mergePDFs(pdfBuffers);

			const safeBytes = new Uint8Array(mergedPdfBytes);

			const blob = new Blob([safeBytes], {type: 'application/pdf'});
			const url = URL.createObjectURL(blob);

			setPdfData(url);
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const getAuctionListData = async () => {
		try {
			const reqBody = getDefaultRequestBody(currentEvent!.id, EVENT_ID);
			await dispatch(getAuctionList({...reqBody, page: 1, pageSize: 10000})).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	useEffect(() => {
		getQRCode();
	}, [currentEvent?.id, itemIds?.length]);

	useEffect(() => {
		if (!currentEvent) return;

		getAuctionListData();
	}, [currentEvent?.id]);

	return (
		<Box sx={{}}>
			{isLoading ? (
				<Box sx={{height: 'calc(100vh - 270px)', position: 'relative'}}>
					<Loader parentClassName={auctionStyle.loader} size={50} />
				</Box>
			) : auctionList?.totalCount && pdfData ? (
				<PdfViewer fileUrl={pdfData} />
			) : (
				<EmptyContent title="Please add at least one auction item to generate bid sheets." icon={JudgeIcon} />
			)}
		</Box>
	);
};

export default BidSheets;
